import { types } from "mobx-state-tree"

// Theme Configuration Model
export const ThemeConfig = types.model("ThemeConfig", {
  // Card variants
  cardVariant: types.optional(
    types.enumeration("CardVariant", ["default", "outlined", "elevated"]),
    "default",
  ),

  // Color scheme
  colorScheme: types.optional(
    types.enumeration("ColorScheme", ["default", "primary", "secondary", "accent"]),
    "default",
  ),

  // Chart colors (can override CSS variables)
  chartColors: types.optional(
    types.model({
      primary: types.optional(types.string, "var(--chart-1)"),
      secondary: types.optional(types.string, "var(--chart-2)"),
      tertiary: types.optional(types.string, "var(--chart-3)"),
      quaternary: types.optional(types.string, "var(--chart-4)"),
      quinary: types.optional(types.string, "var(--chart-5)"),
    }),
    {},
  ),

  // Spacing
  spacing: types.optional(
    types.enumeration("Spacing", ["compact", "normal", "comfortable"]),
    "normal",
  ),

  // Border radius
  borderRadius: types.optional(
    types.enumeration("BorderRadius", ["none", "small", "medium", "large"]),
    "medium",
  ),

  // Typography scale
  typographyScale: types.optional(
    types.enumeration("TypographyScale", ["small", "normal", "large"]),
    "normal",
  ),
})

// Activity Item Model
export const ActivityItem = types.model("ActivityItem", {
  id: types.identifier,
  icon: types.string,
  title: types.string,
  date: types.string,
})

// Metric Model
export const Metric = types.model("Metric", {
  id: types.identifier,
  title: types.string,
  description: types.string,
  value: types.number,
  percentage: types.number,
  trend: types.enumeration("Trend", ["up", "down", "neutral"]),
  chartData: types.array(
    types.model({
      label: types.string,
      value: types.number,
    }),
  ),
  chartType: types.enumeration("ChartType", ["line", "bar"]),
})

// Quick Insight Model
export const QuickInsight = types.model("QuickInsight", {
  id: types.identifier,
  subtitle: types.string,
  date: types.string,
  title: types.string,
  value: types.string,
  secondaryText: types.string,
  chartData: types.array(
    types.model({
      value: types.number,
    }),
  ),
})

// Insight with Table Data Model (for InsightDashboard)
export const InsightWithData = types.model("InsightWithData", {
  id: types.identifier,
  insight: QuickInsight,
  // biome-ignore lint/suspicious/noExplicitAny: Table data can be any shape
  tableData: types.frozen<Array<Record<string, any>>>(),
  // biome-ignore lint/suspicious/noExplicitAny: Column definitions can be any shape
  tableColumns: types.frozen<Array<any>>(),
})

// Insight Dashboard Store
export const InsightDashboardStore = types
  .model("InsightDashboardStore", {
    insights: types.array(InsightWithData),
    selectedInsightId: types.maybe(types.string),
  })
  .views((self) => ({
    get selectedInsight() {
      if (!self.selectedInsightId) {
        return self.insights.length > 0 ? self.insights[0] : undefined
      }
      return self.insights.find((i) => i.id === self.selectedInsightId)
    },
    get selectedTableData() {
      const selected = this.selectedInsight
      return selected ? selected.tableData : []
    },
    get selectedTableColumns() {
      const selected = this.selectedInsight
      return selected ? selected.tableColumns : []
    },
  }))
  .actions((self) => ({
    selectInsight(insightId: string) {
      self.selectedInsightId = insightId
    },
    setInsights(
      insights: Array<{
        id: string
        insight: {
          id: string
          subtitle: string
          date: string
          title: string
          value: string
          secondaryText: string
          chartData: Array<{ value: number }>
        }
        // biome-ignore lint/suspicious/noExplicitAny: Table data can be any shape
        tableData: Array<Record<string, any>>
        // biome-ignore lint/suspicious/noExplicitAny: Column definitions can be any shape
        tableColumns: Array<any>
      }>,
    ) {
      // biome-ignore lint/suspicious/noExplicitAny: MST replace() requires any type
      self.insights.replace(insights as any)
      // Auto-select first insight if none selected
      if (!self.selectedInsightId && insights.length > 0) {
        self.selectedInsightId = insights[0].id
      }
    },
    clearInsights() {
      self.insights.clear()
      self.selectedInsightId = undefined
    },
  }))

// Table Row Model (generic, can be extended)
export const TableRow = types.model("TableRow", {
  id: types.identifier,
  data: types.frozen(), // Flexible data structure
})

// Table Filter Model
export const TableFilter = types.model("TableFilter", {
  id: types.identifier,
  label: types.string,
  value: types.string,
  selected: types.optional(types.boolean, false),
})

// Table Column Config
export const TableColumn = types.model("TableColumn", {
  id: types.identifier,
  header: types.string,
  accessorKey: types.string,
  sortable: types.optional(types.boolean, true),
})

// Main Developer Insights Store
export const DeveloperInsightsStore = types
  .model("DeveloperInsightsStore", {
    activities: types.array(ActivityItem),
    metrics: types.array(Metric),
    quickInsights: types.array(QuickInsight),
    tableData: types.array(TableRow),
    tableColumns: types.array(TableColumn),
    tableFilters: types.array(TableFilter),
    theme: types.optional(ThemeConfig, {}),
  })
  .views((self) => ({
    get filteredTableData() {
      const selectedFilters = self.tableFilters.filter((f) => f.selected).map((f) => f.value)

      if (selectedFilters.length === 0) {
        return self.tableData
      }

      // This is a basic filter implementation
      // In a real app, you'd implement more sophisticated filtering
      return self.tableData
    },
    getActivityById(id: string) {
      return self.activities.find((a) => a.id === id)
    },
    getMetricById(id: string) {
      return self.metrics.find((m) => m.id === id)
    },
    getQuickInsightById(id: string) {
      return self.quickInsights.find((qi) => qi.id === id)
    },
  }))
  .actions((self) => ({
    // Activities
    addActivity(activity: { id: string; icon: string; title: string; date: string }) {
      self.activities.push(activity)
    },
    setActivities(
      activities: Array<{
        id: string
        icon: string
        title: string
        date: string
      }>,
    ) {
      // biome-ignore lint/suspicious/noExplicitAny: MST replace() requires any type
      self.activities.replace(activities as any)
    },

    // Metrics
    addMetric(metric: {
      id: string
      title: string
      description: string
      value: number
      percentage: number
      trend: "up" | "down" | "neutral"
      chartData: Array<{ label: string; value: number }>
      chartType: "line" | "bar"
    }) {
      self.metrics.push(metric)
    },
    setMetrics(
      metrics: Array<{
        id: string
        title: string
        description: string
        percentage: number
        trend: "up" | "down"
        chartType: "line" | "bar"
        data: Array<{ name: string; value: number }>
      }>,
    ) {
      // biome-ignore lint/suspicious/noExplicitAny: MST replace() requires any type
      self.metrics.replace(metrics as any)
    },

    // Quick Insights
    addQuickInsight(insight: {
      id: string
      subtitle: string
      date: string
      title: string
      value: string
      secondaryText: string
      chartData: Array<{ value: number }>
    }) {
      self.quickInsights.push(insight)
    },
    setQuickInsights(
      insights: Array<{
        id: string
        subtitle: string
        date: string
        title: string
        value: string
        secondaryText: string
        chartData: Array<{ value: number }>
      }>,
    ) {
      // biome-ignore lint/suspicious/noExplicitAny: MST replace() requires any type
      self.quickInsights.replace(insights as any)
    },

    // Table Data
    // biome-ignore lint/suspicious/noExplicitAny: Table rows can have dynamic properties
    setTableData(data: Array<{ id: string; data: any }>) {
      // biome-ignore lint/suspicious/noExplicitAny: MST replace() requires any type
      self.tableData.replace(data as any)
    },
    setTableColumns(
      columns: Array<{
        id: string
        header: string
        accessorKey: string
      }>,
    ) {
      // biome-ignore lint/suspicious/noExplicitAny: MST replace() requires any type
      self.tableColumns.replace(columns as any)
    },
    setTableFilters(
      filters: Array<{
        id: string
        label: string
        selected: boolean
      }>,
    ) {
      // biome-ignore lint/suspicious/noExplicitAny: MST replace() requires any type
      self.tableFilters.replace(filters as any)
    },
    toggleFilter(filterId: string) {
      const filter = self.tableFilters.find((f) => f.id === filterId)
      if (filter) {
        filter.selected = !filter.selected
      }
    },

    // Clear all data
    clearAll() {
      self.activities.clear()
      self.metrics.clear()
      self.quickInsights.clear()
      self.tableData.clear()
      self.tableColumns.clear()
      self.tableFilters.clear()
    },

    // Theme Configuration
    setTheme(config: {
      cardVariant?: "default" | "outlined" | "elevated"
      colorScheme?: "default" | "primary" | "secondary" | "accent"
      chartColors?: {
        primary?: string
        secondary?: string
        tertiary?: string
        quaternary?: string
        quinary?: string
      }
      spacing?: "compact" | "normal" | "comfortable"
      borderRadius?: "none" | "small" | "medium" | "large"
      typographyScale?: "small" | "normal" | "large"
    }) {
      if (config.cardVariant) {
        self.theme.cardVariant = config.cardVariant
      }
      if (config.colorScheme) {
        self.theme.colorScheme = config.colorScheme
      }
      if (config.chartColors) {
        if (config.chartColors.primary) {
          self.theme.chartColors.primary = config.chartColors.primary
        }
        if (config.chartColors.secondary) {
          self.theme.chartColors.secondary = config.chartColors.secondary
        }
        if (config.chartColors.tertiary) {
          self.theme.chartColors.tertiary = config.chartColors.tertiary
        }
        if (config.chartColors.quaternary) {
          self.theme.chartColors.quaternary = config.chartColors.quaternary
        }
        if (config.chartColors.quinary) {
          self.theme.chartColors.quinary = config.chartColors.quinary
        }
      }
      if (config.spacing) {
        self.theme.spacing = config.spacing
      }
      if (config.borderRadius) {
        self.theme.borderRadius = config.borderRadius
      }
      if (config.typographyScale) {
        self.theme.typographyScale = config.typographyScale
      }
    },

    resetTheme() {
      self.theme.cardVariant = "default"
      self.theme.colorScheme = "default"
      self.theme.chartColors.primary = "var(--chart-1)"
      self.theme.chartColors.secondary = "var(--chart-2)"
      self.theme.chartColors.tertiary = "var(--chart-3)"
      self.theme.chartColors.quaternary = "var(--chart-4)"
      self.theme.chartColors.quinary = "var(--chart-5)"
      self.theme.spacing = "normal"
      self.theme.borderRadius = "medium"
      self.theme.typographyScale = "normal"
    },
  }))

export type DeveloperInsightsStoreType = typeof DeveloperInsightsStore.Type
export type ActivityItemType = typeof ActivityItem.Type
export type MetricType = typeof Metric.Type
export type QuickInsightType = typeof QuickInsight.Type
export type InsightWithDataType = typeof InsightWithData.Type
export type InsightDashboardStoreType = typeof InsightDashboardStore.Type
export type TableRowType = typeof TableRow.Type
export type TableColumnType = typeof TableColumn.Type
export type TableFilterType = typeof TableFilter.Type
export type ThemeConfigType = typeof ThemeConfig.Type
